import { useRef, useState } from 'react'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
} from '@mui/material'
import { Delete } from '@mui/icons-material'
import { com, kotlin } from '@eidu/web'
import PathPermission from '../../pages/permission/path/PathPermission'
import FieldValueEqualsInput from '../input/FieldValueEqualsInput'
import FieldValueEqualsUserFieldValueInput from '../input/FieldValueEqualsUserFieldValueInput'
import FieldValueEqualsUserEntityIdInput from '../input/FieldValueEqualsUserEntityIdInput'
import SingleInput from '../input/SingleInput'
import { EDITOR_MODE_OPTIONS, EditorMode, editorModeOf } from './utils'
import EntityType = com.eidu.sharedlib.entity.type.EntityType
import EntityTypeId = com.eidu.sharedlib.entity.type.EntityTypeId
import EntitySelector = com.eidu.sharedlib.entity.selector.EntitySelector
import EntityPath = com.eidu.sharedlib.entity.path.EntityPath
import findPathsBetweenTypes = com.eidu.sharedlib.entity.path.findPathsBetweenTypes
import EntityAction = com.eidu.sharedlib.entity.permission.EntityAction
import EntitySelectorPermission = com.eidu.sharedlib.entity.permission.EntitySelectorPermission
import EntityPathPermission = com.eidu.sharedlib.entity.permission.EntityPathPermission
import Permission = com.eidu.sharedlib.entity.permission.Permission
import KtList = kotlin.collections.KtList

type PermissionEditorRowProps = {
  permission: Permission
  allowsPath: boolean
  userEntityTypeId: EntityTypeId | undefined
  entityTypes: readonly EntityType[]
  rowEntityTypeId: EntityTypeId
  action: EntityAction
  onChange: (permission: Permission) => void
  onDelete: () => void
}

const PermissionEditorRow = ({
  permission,
  allowsPath,
  userEntityTypeId,
  entityTypes,
  rowEntityTypeId,
  action,
  onChange,
  onDelete,
}: PermissionEditorRowProps) => {
  const [mode, setMode] = useState<EditorMode>(editorModeOf(permission))
  const [confirmClearOpen, setConfirmClearOpen] = useState(false)
  const pendingModeRef = useRef<EditorMode | null>(null)

  const allowedOptions = allowsPath ? EDITOR_MODE_OPTIONS : EDITOR_MODE_OPTIONS.filter((o) => o.value !== 'path')

  const applyMode = (next: EditorMode) => {
    setMode(next)
    if (next === 'all') {
      onChange(new EntitySelectorPermission(action, new EntitySelector.OfType(rowEntityTypeId)))
      return
    }
    if (next === 'path' && userEntityTypeId) {
      const paths = findPathsBetweenTypes(
        KtList.fromJsArray(entityTypes),
        userEntityTypeId,
        rowEntityTypeId
      ).asJsReadonlyArrayView()
      if (paths.length > 0) onChange(new EntityPathPermission(action, paths[0]))
    }
    // For fve / fveUserField / fveUserEntity, the corresponding sub-input
    // commits the new permission once the user fills it in.
  }

  const handleModeChange = (next: EditorMode) => {
    if (next === mode) return
    if (mode === 'path' && next !== 'path' && permission instanceof EntityPathPermission) {
      pendingModeRef.current = next
      setConfirmClearOpen(true)
      return
    }
    applyMode(next)
  }

  const singleSelector =
    permission instanceof EntitySelectorPermission && permission.targets instanceof EntitySelector.Single
      ? permission.targets
      : undefined
  const fveSelector =
    permission instanceof EntitySelectorPermission && permission.targets instanceof EntitySelector.FieldValueEquals
      ? permission.targets
      : undefined
  const fveUserFieldSelector =
    permission instanceof EntitySelectorPermission &&
    permission.targets instanceof EntitySelector.FieldValueEqualsUserFieldValue
      ? permission.targets
      : undefined
  const fveUserEntitySelector =
    permission instanceof EntitySelectorPermission &&
    permission.targets instanceof EntitySelector.FieldValueEqualsUserEntityId
      ? permission.targets
      : undefined

  return (
    <Stack
      direction={{ xs: 'column', md: 'row' }}
      spacing={1}
      alignItems={{ xs: 'stretch', md: 'center' }}
      sx={{ p: 1, border: 1, borderColor: 'divider', borderRadius: 1, bgcolor: 'background.paper' }}
    >
      <FormControl size="small" sx={{ minWidth: 200, flexShrink: 0 }}>
        <InputLabel id={`permission-mode-${rowEntityTypeId.toString()}-${action.toString()}`}>Filter</InputLabel>
        <Select
          labelId={`permission-mode-${rowEntityTypeId.toString()}-${action.toString()}`}
          label="Filter"
          size="small"
          value={mode}
          onChange={(e) => handleModeChange(e.target.value as EditorMode)}
          data-testid="permission-mode-select"
        >
          {allowedOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {mode === 'path' ? (
          <PathPermission
            fromEntityTypeId={userEntityTypeId}
            preselectedPath={permission instanceof EntityPathPermission ? permission.path : undefined}
            entityTypes={entityTypes}
            onChange={(path: EntityPath | undefined) => {
              if (path) onChange(new EntityPathPermission(action, path))
            }}
            lockedToTypeId={rowEntityTypeId}
          />
        ) : mode === 'single' ? (
          <SingleInput
            entityTypeId={rowEntityTypeId}
            selector={singleSelector}
            setSelector={(s) => onChange(new EntitySelectorPermission(action, s))}
            disabled={false}
          />
        ) : mode === 'fve' ? (
          <FieldValueEqualsInput
            entityTypeId={rowEntityTypeId}
            selector={fveSelector}
            setSelector={(s) => onChange(new EntitySelectorPermission(action, s))}
            disabled={false}
          />
        ) : mode === 'fveUserField' ? (
          <FieldValueEqualsUserFieldValueInput
            entityTypeId={rowEntityTypeId}
            allEntityTypes={entityTypes}
            selector={fveUserFieldSelector}
            setSelector={(s) => onChange(new EntitySelectorPermission(action, s))}
            disabled={false}
          />
        ) : mode === 'fveUserEntity' ? (
          <FieldValueEqualsUserEntityIdInput
            entityTypeId={rowEntityTypeId}
            selector={fveUserEntitySelector}
            setSelector={(s) => onChange(new EntitySelectorPermission(action, s))}
            disabled={false}
          />
        ) : null}
      </Box>
      <IconButton size="small" onClick={onDelete} aria-label="Remove permission">
        <Delete fontSize="small" />
      </IconButton>
      <Dialog open={confirmClearOpen} onClose={() => setConfirmClearOpen(false)}>
        <DialogTitle>Clear assignment?</DialogTitle>
        <DialogContent>
          <DialogContentText>Switching the filter will discard the current assignment. Are you sure?</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmClearOpen(false)}>Cancel</Button>
          <Button
            color="error"
            onClick={() => {
              setConfirmClearOpen(false)
              const next = pendingModeRef.current ?? 'all'
              pendingModeRef.current = null
              applyMode(next)
            }}
          >
            Clear assignment
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  )
}

export default PermissionEditorRow
